import React, {useEffect, useState, useContext } from "react";
import SellerSidebar from "../../../navigation/sellerNavigation";
import FormInput from "../../../props/propertyFormProp";
import ImageUpload from "../../../utility/imageUploader";
import { postDataForm } from "../../../fetchRoutes/postDataForm";
import SellerMobileSidebar from "../../../navigation/sellerMobileNavigation";
import { DeviceContext } from "../../../context/deviceContext";

export default function AddPropertyContainer() {

    const addProperty = process.env.REACT_APP_ADD_PROPERTY;

    const [property, setProperty] = useState({
        title: '',
        description: '',
        price: '',
        address: '',
        rooms: '',
        bathrooms: '',
        receptions: '',
        legalTerms: '',
        disclaimer: '',
        termsForSale: '',
        images: []
    });

    const {isMobile}= useContext(DeviceContext);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [formStep, setFormStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const [errors, setErrors] = useState({});
    const [isValid, setIsValid] = useState(false);

    
    const handleChange = (event) => {
        const { name, value } = event.target;
        setProperty({ ...property, [name]: value });

        // Clear the error when the user starts typing
        setErrors((prev) => ({ ...prev, [name]: "" }));
        validateFields(name, value);
    };

    const handleImageUpload = (newImageUrls) => {
        setProperty({ ...property, images: newImageUrls });
    };

    const nextStep = () => {
        setFormStep(2);
    };

    const prevStep = () => {
        setFormStep(1);
    };

    const canProceed = () => {
        return (
            property.title &&
            property.description.length>=100 &&
            property.price &&
            property.address &&
            property.rooms &&
            property.bathrooms &&
            property.receptions
        );
    };

    const nextImage = () => {
        setCurrentImageIndex((prevIndex) => (prevIndex + 1) % property.images.length);
    };

    const prevImage = () => {
        setCurrentImageIndex((prevIndex) => 
            prevIndex === 0 ? property.images.length - 1 : prevIndex - 1
        );
    };

    const getShortDescription = (desc) => {
        return desc.split(".").slice(0, 1).join(".") + ".";
    };

    const validateFields = (name, value) => {
        let message = "";
    
        // Skip validation for empty fields
        if (!value) {
            setErrors((prevErrors) => ({
                ...prevErrors,
                [name]: "", // Clear any existing error for this field
            }));
            return;
        }
    
        // Validation rules
        switch (name) {
            case "title":
                message = value.length < 3 ? "Must be at least 3 characters long" : "";
                break;
            case "description":
                message = value.length < 100 ? "Must be at least 100 characters long" : "";
                break;
            case "price":
                message = isNaN(value) || value <= 100 ? "Must be a number over 100" : "";
                break;
            case "address":
                message = value.length <= 2 ? "Valid address is required" : "";
                break;
            case "rooms":
            case "bathrooms":
            case "receptions":
                message = isNaN(value) || value < 0 ? "Must be >=0" : "";
                break;
            case "legalTerms":
                message =value.length < 100 ? "Must be at least 100 characters long" : "";
                break;
            case "disclaimer":
                message =value.length < 100 ? "Must be at least 100 characters long" : "";
                break;
            case "termsForSale":
                message = value.length < 100 ? "Must be at least 100 characters long" : "";
                break;
            default:
                message = "";
        }
    
        // Update errors state
        setErrors((prevErrors) => ({
            ...prevErrors,
            [name]: message,
        }));
    };
    


    useEffect(() => {
        const validateForm = () => {
            const newErrors = {};
    
            // Validate only fields with non-empty values
            Object.keys(property).forEach((key) => {
                if (property[key]) {
                    validateFields(key, property[key]);
                    if (errors[key]) {
                        newErrors[key] = errors[key];
                    }
                }
            });
    
            // Check if there are NO errors
            const hasNoErrors = Object.keys(newErrors).length === 0;
            setIsValid(hasNoErrors);
        };
    
        validateForm();
        // eslint-disable-next-line
    }, [property]); 
    

    // Add property
    const handleFormSubmit = async (event) => {
        event.preventDefault();
    
        setIsLoading(true);
        setSuccessMessage("");
    
        if (!isValid) {
            setErrorMessage("Please fill in all required fields correctly.");
            setTimeout(() => {
                setErrorMessage("");
            }, 3000);
            setIsLoading(false);
            return;
        }
    
        try {
            const formData = new FormData();
    
            // Append form fields
            formData.append("title", property.title);
            formData.append("description", property.description);
            formData.append("price", property.price);
            formData.append("address", property.address);
            formData.append("rooms", property.rooms);
            formData.append("bathrooms", property.bathrooms);
            formData.append("receptions", property.receptions);
            formData.append("legalTerms", property.legalTerms);
            formData.append("disclaimer", property.disclaimer);
            formData.append("termsForSale", property.termsForSale);
    
            // Function to convert blob URL to File object
            const blobToFile = async (blobUrl, fileName) => {
                const response = await fetch(blobUrl);
                const blob = await response.blob();
                return new File([blob], fileName, { type: blob.type });
            };
    
            // Convert all blob URLs to File objects before appending
            const addFilesToFormData = async () => {
                const filePromises = property.images.map((blobUrl, index) =>
                    blobToFile(blobUrl, `image_${index + 1}.png`)  // Convert each blob to File
                );
    
                const files = await Promise.all(filePromises);
    
                // Append converted files to FormData
                files.forEach((file) => {
                    formData.append("images", file);
                });
            };
    
            // Wait until all files are appended before making the request
            await addFilesToFormData();
    
    
            // Make the API request with the formData
            const response = await postDataForm(addProperty, formData);
    
            console.log("Server response:", response);
    
            if (response.status === "Success") {
                setSuccessMessage("Property added successfully!");
    
                setTimeout(() => {
                    setSuccessMessage("");
                }, 3000);
    
                //Reset form fields
                setProperty({
                    title: "",
                    description: "",
                    price: "",
                    address: "",
                    rooms: "",
                    bathrooms: "",
                    receptions: "",
                    legalTerms: "",
                    disclaimer: "",
                    termsForSale: "",
                    images: []
                });
    
                // Reset form step and image index
                setFormStep(1);
                setCurrentImageIndex(0);
            } else {
                setErrorMessage(response.message);
                setTimeout(() => {
                    setErrorMessage("");
                }, 3000);
            }
    
        } catch (error) {
            console.error("Error submitting form:", error);
            setErrorMessage("Failed to add property. Please try again.");
            setTimeout(() => {
                setErrorMessage("");
            }, 3000);
        } finally {
            setIsLoading(false);
        }
    };
    

    return (
        <main className="sellerDashboard">
            {isMobile ? <SellerMobileSidebar/> :<SellerSidebar />}

            {isLoading && (
                <div className="loaderContainer">
                    <div className="loader"></div>
                    <p className="loadingText">Submitting your property...</p>
                </div>
            )}

            <div className="addPropertyContainer">
                
                <div className="addProperty">
                    
                    {/* Success Message */}
                    <div className={`${successMessage ? "successMessageAdd" : "hiddes"} ${errorMessage ? "errorMessage" : "hidden"}`}>
                        {successMessage && successMessage} {errorMessage && errorMessage} 
                    </div>

                
                    <div className="formTitle">
                        <h1 className="addTitle">Add Property</h1>
                        <div className="underline"></div>                      
                    </div>
                  
                    
                    {formStep === 1 && (
                        <form className="addPropertyForm" onSubmit={handleFormSubmit}>

                            <FormInput 
                                label="Title" 
                                name="title" 
                                type="text" 
                                placeholder="Enter the title" 
                                required={true}
                                value={property.title} 
                                onChange={handleChange} 
                                className={errors.title ? "error" : ""}
                                errorMessage={errors.title}
                            />

                            <div className="inputs">
                                <label className="label">
                                    Description <span className="asterisk">* {errors.description}</span>
                                </label>
                                <textarea
                                    className={`fullName propertyDesc ${errors.description ? "error" : ""}`}
                                    type="text"
                                    name="description"
                                    placeholder="Enter property description"
                                    required
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    minLength="100"
                                    maxLength="2000"
                                    value={property.description}
                                    onChange={handleChange}
                                />
                            </div>


                            <FormInput 
                                label="Price" 
                                name="price" 
                                type="number" 
                                placeholder="Enter price" 
                                value={property.price} 
                                onChange={handleChange} 
                                className={errors.price ? "error" : ""}
                                errorMessage={errors.price}
                                required={true}

                            />

                            <FormInput 
                                label="Address" 
                                name="address" 
                                type="text" 
                                placeholder="XX4 6XX LONDON" 
                                value={property.address} 
                                onChange={handleChange}
                                className={errors.address ? "error" : ""}
                                errorMessage={errors.address} 
                                required={true}

                            />

                            <div className="rooms">
                                <FormInput 
                                    label="Bedrooms" 
                                    name="rooms" 
                                    type="number" 
                                    placeholder="Bedrooms" 
                                    value={property.rooms} 
                                    onChange={handleChange} 
                                    className={errors.rooms ? "error" : ""}
                                    errorMessage={errors.rooms}
                                    required={true}

                                />

                                <FormInput 
                                    label="Bathrooms" 
                                    name="bathrooms" 
                                    type="number" 
                                    placeholder="Bathrooms" 
                                    value={property.bathrooms} 
                                    onChange={handleChange}
                                    className={errors.bathrooms ? "error" : ""}
                                    errorMessage={errors.bathrooms} 
                                    required={true}

                                />

                                <FormInput 
                                    label="Receptions" 
                                    name="receptions" 
                                    type="number" 
                                    placeholder="Receptions" 
                                    value={property.receptions} 
                                    onChange={handleChange} 
                                    errorMessage={errors.receptions}
                                    className={errors.receptions ? "error" : ""}
                                    required={true}

                                />
                            </div>
                            {canProceed() && (
                                <button type="button" className={`goNext ${!isValid ? 'disabled' : ''}`} onClick={nextStep} disabled={!isValid}>
                                    Next →
                                </button>
                            )}
                        </form>
                    )}

                    {formStep === 2 && (
                        <form className="addPropertyForm" onSubmit={handleFormSubmit}>
                            <div className="inputs">
                                <label className="label">
                                    Legal Terms <span className="asterisk">* {errors.legalTerms}</span>
                                </label>
                                <textarea
                                    className={`fullName propertyDesc ${errors.legalTerms ? "error" : ""}`}
                                    type="text"
                                    name="legalTerms"
                                    placeholder="Enter the legal terms and conditions related to this property sale, including any specific laws or agreements."
                                    required
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    minLength="100"
                                    maxLength="2000"
                                    value={property.legalTerms}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="inputs">
                                <label className="label">
                                    Disclaimer <span className="asterisk">* {errors.disclaimer}</span>
                                </label>
                                <textarea
                                    className={`fullName propertyDesc ${errors.disclaimer ? "error" : ""}`}
                                    type="text"
                                    name="disclaimer"
                                    placeholder="Enter any disclaimers or important information related to the property, such as potential risks or legal limitations."
                                    required
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    minLength="100"
                                    maxLength="2000"
                                    value={property.disclaimer}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="inputs">
                                <label className="label">
                                    Terms for Sale <span className="asterisk">* {errors.termsForSale}</span>
                                </label>
                                <textarea
                                    className={`fullName propertyDesc ${errors.termsForSale ? "error" : ""}`}
                                    type="text"
                                    name="termsForSale"
                                    placeholder="Provide the specific terms and conditions for the sale of the property, such as payment terms, warranties, or seller obligations."
                                    required
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    minLength="100"
                                    maxLength="2000"
                                    value={property.termsForSale}
                                    onChange={handleChange}
                                />
                            </div>
                           
                            {/* Handle image uplaod */}
                            <ImageUpload onUpload={handleImageUpload} />

                            <div className="formNavigation">
                                <button type="button" className="goBack" onClick={prevStep}>
                                    ← Back
                                </button>
                                <button type="submit" className="submitPropertyButton">
                                    Submit
                                </button>
                            </div>
                        </form>
                    )}
                </div>

               {/* Preview Form */}
                <div className="addPropertyPreview">
                    <div className="previewCard">
                        <div className="imageSlider">
                            {property.images.length > 0 ? (
                                <>
                                    <button onClick={prevImage} className="arrowsButton">‹</button>
                                    <img src={property.images[currentImageIndex]} alt="Property Preview" />
                                    <button onClick={nextImage} className="arrowsButton">›</button>
                            
                                </>
                            ) : (
                                <div className="noImage">
                                    <p>No Image Available</p>
                                </div>
                            )}
                        </div>
                        <div className="propertyDetails">
                            <h2 className="propTitle">{property.title || "Property Title"}</h2>
                            <p className="demoPrice">{property.price ? `£${property.price}` : "Property Price"}</p>
                            <p className="propRooms">{property.rooms} Beds | {property.bathrooms} Baths | {property.receptions} Receptions</p>
                            <p className="shortDescription">{property.description ? getShortDescription(property.description) : "Property description preview..."}</p>
                        </div>
                    </div>
                </div>
            
            </div>
        </main>
    );
}

